/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import React, { useCallback, useEffect } from "react";

import { usePlayerComponents } from "../../../../../../shared/context/PlayerComponentsContext";
import { usePlayerTranslation } from "../../../../../../shared/context/PlayerTranslationContext";
import PlayIcon from "../../../../../../shared/icons/player/Play.svg";
import { MediaImageType } from "../../../../../../shared/types/enums";
import { getImageByType } from "../../../../../../shared/utils/image";
import { usePlayerCurrentMedia } from "../../../../../Controller";
import { useCustomPlayerContext } from "../../../../../CustomPlayerContext";
import {
  DropdownHeader,
  DropdownMenuItem,
  DropdownMenuItemExpandable,
  DropdownMenuSection,
  useActiveSection,
  useToggleVisibility,
} from "../../../../Base";

import { useSeason } from "./useSeason";

const spinnerStyle = { margin: "15px" };
const spinnerSize = 50;

export const MainSection = () => {
  const { seriesMedia, activeSeason, parentMedia } = useSeason();

  const { visible } = useToggleVisibility();
  const { resetToDefault } = useActiveSection();
  const { media } = usePlayerCurrentMedia();
  const { Button, LoaderSpinner } = usePlayerComponents();
  const { t } = usePlayerTranslation();
  const { onNavigateToMedia } = useCustomPlayerContext();

  useEffect(() => resetToDefault(), [resetToDefault, visible]);

  const renderContent = useCallback(() => {
    if (parentMedia.IsLoading || seriesMedia.IsLoading) {
      return (
        <LoaderSpinner
          width={spinnerSize}
          height={spinnerSize}
          style={spinnerStyle}
        />
      );
    }

    if (Object.keys(seriesMedia).length === 0) {
      return parentMedia.Media?.Media?.map((episode) => {
        const imageData = getImageByType(
          episode?.Images || [],
          MediaImageType.Frame,
        );
        return (
          <DropdownMenuItemExpandable
            key={episode.Id}
            title={episode.Title}
            orderInParent={episode.OrderInParent}
          >
            <div className="SeasonsSection__Content">
              <div className="SeasonsSection__Content__Thumbnail">
                {imageData?.Url && (
                  <img
                    src={imageData.Url}
                    alt={episode.Title || ""}
                    className="SeasonsSection__image"
                  />
                )}
                {media.Id !== episode.Id && (
                  <div className="SeasonsSection__Content__Thumbnail__Control">
                    <Button
                      variant="secondary"
                      onClick={() => onNavigateToMedia?.(episode)}
                      aria-label={
                        episode.Title
                          ? `${t("PLAYER_TOOLTIP_PLAY")} ${episode.Title}`
                          : t("PLAYER_TOOLTIP_PLAY")
                      }
                      iconOnly
                    >
                      <PlayIcon aria-hidden="true" focusable="false" />
                    </Button>
                  </div>
                )}
              </div>
              <div className="SeasonsSection__Content__Metadata">
                <div
                  className="SeasonsSection__Content__Metadata__Description"
                  dangerouslySetInnerHTML={{
                    __html: episode.ShortDescription || "",
                  }}
                />
              </div>
            </div>
          </DropdownMenuItemExpandable>
        );
      });
    }

    return seriesMedia.Media?.Media?.map((season) => (
      <DropdownMenuItem
        key={season.Id}
        navigateToSection="season"
        active={season.Id === activeSeason}
        resetToDefaultSectionAfterSelect
        showActiveIcon
        seasonId={season.Id.toString()}
      >
        {season.Title}
      </DropdownMenuItem>
    ));
  }, [seriesMedia, parentMedia]);

  return (
    <DropdownMenuSection name="seasons">
      <DropdownHeader>{seriesMedia.Media?.Title}</DropdownHeader>
      {renderContent()}
    </DropdownMenuSection>
  );
};
